/**
 * KPI Section component that wraps a group of KPI cards.
 * Extracted from KPIOverviewTab.tsx for better code organization.
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';
import { KpiMetric, LinkedReportInfo } from '../utils/kpiHelpers';
import KPICard from './KPICard';

interface KPISectionProps {
	title: string;
	countLabel?: string;
	metrics: KpiMetric[];
	emptyMessage: string;
	columns: number;
	onOpenMetric: (metric: KpiMetric) => void;
	searchTerm?: string;
	getLinkedReport?: (metric: KpiMetric) => LinkedReportInfo | null;
	/** Optional: route Power BI / Tableau badge taps to the Reports tab instead of the external link. */
	onNavigateToReports?: (kpiName: string) => void;
}

const EmptyState: React.FC<{ message: string }> = ({ message }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	return (
		<View style={[styles.emptyState, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
			<Text style={[styles.emptyStateText, isDark && { color: colors.text.secondary }]}>{message}</Text>
		</View>
	);
};

const KPISection: React.FC<KPISectionProps> = ({ title, countLabel, metrics, emptyMessage, columns, onOpenMetric, searchTerm, getLinkedReport, onNavigateToReports }) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';

	return (
		<View style={styles.sectionContainer}>
			<View style={styles.sectionHeader}>
				<View style={styles.sectionTitleRow}>
					<MaterialIcons name="error" size={20} color="#E74A1A" />
					<Text style={[styles.sectionTitle, isDark && { color: colors.text.primary }]}>{title}</Text>
				</View>
				{countLabel ? (
					<View style={[styles.countPill, isDark && { backgroundColor: 'rgba(220,53,53,0.18)' }]}>
						<Text style={[styles.countPillText, isDark && { color: '#FF8A80' }]}>{countLabel}</Text>
					</View>
				) : null}
			</View>

			{metrics.length === 0 ? (
				<EmptyState message={emptyMessage} />
			) : (
				<View style={styles.cardsGrid}>
					{metrics.map(item => (
						<View key={`${item.name}-${item.category}`} style={[styles.cardColumn, { width: `${100 / columns}%` }]}>
							<KPICard
								metric={item}
								onOpen={onOpenMetric}
								searchTerm={searchTerm}
								linkedReport={getLinkedReport?.(item) ?? null}
								onNavigateToReports={onNavigateToReports}
							/>
						</View>
					))}
				</View>
			)}
		</View>
	);
};

export { EmptyState };

const styles = StyleSheet.create({
	sectionContainer: {
		marginTop: 12,
	},
	sectionHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 10,
	},
	sectionTitleRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	sectionTitle: {
		fontSize: 16,
		fontFamily: FontFamily.bold,
		color: '#2D2F33',
		letterSpacing: 0.3,
		lineHeight: 22,
	},
	countPill: {
		backgroundColor: '#F9E4D7',
		paddingHorizontal: 10,
		paddingVertical: 4,
		borderRadius: 14,
	},
	countPillText: {
		fontSize: 13,
		fontFamily: FontFamily.bold,
		color: '#D14343',
	},
	cardsGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
	},
	cardColumn: {
		padding: 8,
	},
	emptyState: {
		paddingVertical: 22,
		paddingHorizontal: 14,
		borderWidth: 1,
		borderColor: '#E3E9F3',
		borderRadius: 10,
		backgroundColor: '#FFFFFF',
	},
	emptyStateText: {
		fontSize: 15,
		fontFamily: FontFamily.medium,
		color: '#6B6E72',
	},
});

export default KPISection;
